import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { siteConfig } from '@/data/mock';

const Datenschutz = () => {
  return (
    <div className="min-h-screen pt-20 bg-[hsl(210,40%,98%)]">
      {/* Header */}
      <section className="py-12 bg-white border-b border-[hsl(210,25%,92%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button asChild variant="ghost" className="mb-6 text-[hsl(215,15%,45%)] hover:text-[hsl(213,80%,35%)]">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zur Startseite
            </Link>
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)]">
            Datenschutzerklärung
          </h1>
          <p className="mt-4 text-[hsl(215,15%,45%)]">
            gemäß Art. 13 und 14 der Datenschutz-Grundverordnung (DSGVO) sowie § 165 Telekommunikationsgesetz 2021 (TKG 2021)
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          {/* Verantwortlicher */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">1. Verantwortlicher</h2>
              <div className="text-[hsl(215,15%,45%)] space-y-4">
                <p>Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) ist:</p>
                <div className="bg-[hsl(210,40%,98%)] p-4 rounded-lg">
                  <p className="font-medium text-[hsl(215,25%,15%)]">
                    Verein Väternotruf.com<br />
                    Rabenhof 16<br />
                    3525 Sallingberg<br />
                    Österreich
                  </p>
                  <div className="mt-3 space-y-1">
                    <p className="flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      0660 233 1560
                    </p>
                    <p className="flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      kontakt@vaeternotruf.com
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Grundlagen */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">2. Grundsätze der Datenverarbeitung</h2>
              <div className="text-[hsl(215,15%,45%)] space-y-4">
                <p>
                  Wir verarbeiten personenbezogene Daten im Einklang mit den Bestimmungen der Datenschutz-Grundverordnung (DSGVO), 
                  des österreichischen Datenschutzgesetzes (DSG) sowie des Telekommunikationsgesetzes 2021 (TKG 2021).
                </p>
                <p>
                  Personenbezogene Daten sind alle Informationen, die sich auf eine identifizierte oder identifizierbare 
                  natürliche Person beziehen. Dazu gehören beispielsweise Name, Adresse, E-Mail-Adresse oder Telefonnummer.
                </p>
                <p>
                  Wir verarbeiten Ihre Daten nur, wenn mindestens eine der folgenden Bedingungen erfüllt ist:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Sie haben Ihre Einwilligung erteilt (Art. 6 Abs. 1 lit. a DSGVO)</li>
                  <li>Die Verarbeitung ist für die Erfüllung eines Vertrags erforderlich (Art. 6 Abs. 1 lit. b DSGVO)</li>
                  <li>Die Verarbeitung ist zur Erfüllung einer rechtlichen Verpflichtung erforderlich (Art. 6 Abs. 1 lit. c DSGVO)</li>
                  <li>Die Verarbeitung ist zur Wahrung berechtigter Interessen erforderlich (Art. 6 Abs. 1 lit. f DSGVO)</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Datenerfassung Website */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">3. Datenerfassung auf dieser Website</h2>
              <div className="text-[hsl(215,15%,45%)] space-y-6">
                
                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">3.1 Server-Log-Dateien</h3>
                  <p className="mb-3">
                    Der Provider dieser Website erhebt und speichert automatisch Informationen in sogenannten Server-Log-Dateien, 
                    die Ihr Browser automatisch an uns übermittelt. Dies sind:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Browsertyp und Browserversion</li>
                    <li>Verwendetes Betriebssystem</li>
                    <li>Referrer URL (die zuvor besuchte Seite)</li>
                    <li>Hostname des zugreifenden Rechners</li>
                    <li>Uhrzeit der Serveranfrage</li>
                    <li>IP-Adresse (anonymisiert)</li>
                  </ul>
                  <p className="mt-3">
                    Diese Daten sind nicht bestimmten Personen zuordenbar. Eine Zusammenführung dieser Daten mit anderen 
                    Datenquellen wird nicht vorgenommen. Die Erfassung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">3.2 Kontaktformular</h3>
                  <p>
                    Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Formular 
                    einschließlich der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den 
                    Fall von Anschlussfragen bei uns gespeichert.
                  </p>
                  <p className="mt-3">
                    <strong>Verarbeitete Daten:</strong> Name, E-Mail-Adresse, Telefonnummer (optional), Nachrichteninhalt<br />
                    <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung) bzw. Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)<br />
                    <strong>Speicherdauer:</strong> Bis zur vollständigen Bearbeitung Ihrer Anfrage, maximal 3 Jahre
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">3.3 Terminbuchung</h3>
                  <p>
                    Bei der Buchung eines Beratungstermins über unser Online-Buchungssystem werden folgende Daten erhoben:
                  </p>
                  <p className="mt-3">
                    <strong>Verarbeitete Daten:</strong> Name, E-Mail-Adresse, Telefonnummer, gewählter Termin, 
                    optionale Beschreibung des Anliegens<br />
                    <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung)<br />
                    <strong>Speicherdauer:</strong> Bis 6 Monate nach Abschluss der Beratung
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cookies */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">4. Cookies</h2>
              <div className="text-[hsl(215,15%,45%)] space-y-4">
                <p>
                  Unsere Website verwendet Cookies. Cookies sind kleine Textdateien, die auf Ihrem Endgerät gespeichert 
                  werden und die Ihr Browser speichert. Sie richten keinen Schaden an.
                </p>
                
                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">4.1 Notwendige Cookies</h3>
                  <p>
                    Diese Cookies sind für die Funktion der Website erforderlich und können nicht deaktiviert werden. 
                    Sie speichern keine personenbezogenen Daten.
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>cookie_consent:</strong> Speichert Ihre Cookie-Einstellungen (Speicherdauer: 1 Jahr)</li>
                    <li><strong>admin_token:</strong> Authentifizierung für den Administrationsbereich (Speicherdauer: Session)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">4.2 Funktionale Cookies</h3>
                  <p>
                    Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung. Sie werden nur mit Ihrer 
                    Zustimmung gesetzt.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">4.3 Analyse-Cookies</h3>
                  <p>
                    Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren. 
                    Alle Daten werden anonymisiert erfasst. Sie werden nur mit Ihrer Zustimmung gesetzt.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">4.4 Cookie-Einstellungen ändern</h3>
                  <p>
                    Sie können Ihre Cookie-Einstellungen jederzeit ändern, indem Sie die Cookies in Ihrem Browser löschen. 
                    Beim nächsten Besuch wird Ihnen das Cookie-Banner erneut angezeigt.
                  </p>
                </div>

                <p className="text-sm bg-[hsl(210,40%,98%)] p-4 rounded-lg">
                  <strong>Rechtsgrundlage:</strong> Notwendige Cookies: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse). 
                  Alle anderen Cookies: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) gemäß § 165 Abs. 3 TKG 2021.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Ihre Rechte */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">5. Ihre Rechte</h2>
              <div className="text-[hsl(215,15%,45%)] space-y-4">
                <p>Sie haben gegenüber uns folgende Rechte hinsichtlich der Sie betreffenden personenbezogenen Daten:</p>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-1">5.1 Auskunftsrecht (Art. 15 DSGVO)</h3>
                    <p>Sie haben das Recht, Auskunft über Ihre von uns verarbeiteten personenbezogenen Daten zu verlangen.</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-1">5.2 Recht auf Berichtigung (Art. 16 DSGVO)</h3>
                    <p>Sie haben das Recht, unverzüglich die Berichtigung unrichtiger oder die Vervollständigung Ihrer Daten zu verlangen.</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-1">5.3 Recht auf Löschung (Art. 17 DSGVO)</h3>
                    <p>Sie haben das Recht, die Löschung Ihrer Daten zu verlangen, sofern nicht gesetzliche Aufbewahrungspflichten entgegenstehen.</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-1">5.4 Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</h3>
                    <p>Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer Daten zu verlangen.</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-1">5.5 Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</h3>
                    <p>Sie haben das Recht, Ihre Daten in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten.</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-1">5.6 Widerspruchsrecht (Art. 21 DSGVO)</h3>
                    <p>
                      Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit gegen 
                      die Verarbeitung Ihrer Daten Widerspruch einzulegen.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-1">5.7 Recht auf Widerruf der Einwilligung (Art. 7 Abs. 3 DSGVO)</h3>
                    <p>
                      Sie haben das Recht, eine erteilte Einwilligung jederzeit zu widerrufen. Die Rechtmäßigkeit der bis zum 
                      Widerruf erfolgten Verarbeitung bleibt davon unberührt.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Beschwerderecht */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">6. Beschwerderecht bei der Aufsichtsbehörde</h2>
              <div className="text-[hsl(215,15%,45%)] space-y-4">
                <p>
                  Wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer personenbezogenen Daten gegen das Datenschutzrecht 
                  verstößt, haben Sie das Recht, sich bei der zuständigen Aufsichtsbehörde zu beschweren (Art. 77 DSGVO).
                </p>
                <div className="bg-[hsl(210,40%,98%)] p-4 rounded-lg">
                  <p className="font-medium text-[hsl(215,25%,15%)]">
                    Österreichische Datenschutzbehörde<br />
                    Barichgasse 40-42<br />
                    1030 Wien<br />
                    Österreich
                  </p>
                  <div className="mt-3">
                    <p>Telefon: +43 1 52 152-0</p>
                    <p>E-Mail: dsb@dsb.gv.at</p>
                    <p>Website: <a href="https://www.dsb.gv.at" target="_blank" rel="noopener noreferrer" className="text-[hsl(213,80%,35%)] hover:underline">www.dsb.gv.at</a></p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Datensicherheit */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">7. Datensicherheit</h2>
              <div className="text-[hsl(215,15%,45%)] space-y-4">
                <p>
                  Wir verwenden innerhalb des Website-Besuchs das verbreitete SSL-Verfahren (Secure Socket Layer) in 
                  Verbindung mit der jeweils höchsten Verschlüsselungsstufe, die von Ihrem Browser unterstützt wird.
                </p>
                <p>
                  Ob eine einzelne Seite unserer Website verschlüsselt übertragen wird, erkennen Sie an der geschlossenen 
                  Darstellung des Schlüssel- beziehungsweise Schloss-Symbols in der Adresszeile Ihres Browsers.
                </p>
                <p>
                  Wir setzen darüber hinaus geeignete technische und organisatorische Sicherheitsmaßnahmen ein, um Ihre 
                  Daten gegen zufällige oder vorsätzliche Manipulationen, teilweisen oder vollständigen Verlust, 
                  Zerstörung oder gegen den unbefugten Zugriff Dritter zu schützen.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Weitergabe */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">8. Weitergabe von Daten</h2>
              <div className="text-[hsl(215,15%,45%)] space-y-4">
                <p>
                  Eine Übermittlung Ihrer persönlichen Daten an Dritte zu anderen als den im Folgenden aufgeführten 
                  Zwecken findet nicht statt.
                </p>
                <p>Wir geben Ihre persönlichen Daten nur an Dritte weiter, wenn:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Sie Ihre ausdrückliche Einwilligung dazu erteilt haben (Art. 6 Abs. 1 lit. a DSGVO)</li>
                  <li>Die Weitergabe zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist</li>
                  <li>Eine gesetzliche Verpflichtung zur Weitergabe besteht (Art. 6 Abs. 1 lit. c DSGVO)</li>
                </ul>
                <p>
                  <strong>Hinweis:</strong> Im Rahmen der vermittelten Rechtsberatung werden Ihre Daten nur mit Ihrer 
                  ausdrücklichen Zustimmung an Rechtsanwälte weitergegeben.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Keine Rechtsberatung */}
          <Card className="border-[hsl(35,70%,80%)] bg-[hsl(35,90%,98%)]">
            <CardContent className="p-8">
              <div className="flex items-start">
                <Shield className="w-6 h-6 text-[hsl(35,90%,40%)] mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-bold text-[hsl(35,50%,25%)] mb-4">Wichtiger Hinweis</h2>
                  <p className="text-[hsl(35,40%,35%)]">
                    Die auf dieser Website angebotenen Dienstleistungen stellen <strong>keine Rechtsberatung</strong> dar. 
                    Der Verein Väternotruf.com ist keine Rechtsanwaltskanzlei. Alle Informationen basieren auf persönlicher 
                    Erfahrung und ersetzen nicht die Beratung durch einen zugelassenen Rechtsanwalt.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Aktualität */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">9. Aktualität und Änderung dieser Datenschutzerklärung</h2>
              <div className="text-[hsl(215,15%,45%)] space-y-4">
                <p>
                  Diese Datenschutzerklärung ist aktuell gültig und hat den Stand: <strong>Dezember 2025</strong>.
                </p>
                <p>
                  Durch die Weiterentwicklung unserer Website oder aufgrund geänderter gesetzlicher beziehungsweise 
                  behördlicher Vorgaben kann es notwendig werden, diese Datenschutzerklärung zu ändern. Die jeweils 
                  aktuelle Datenschutzerklärung kann jederzeit auf dieser Website abgerufen werden.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Kontakt */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">10. Kontakt für Datenschutzanfragen</h2>
              <div className="text-[hsl(215,15%,45%)] space-y-4">
                <p>
                  Bei Fragen zur Erhebung, Verarbeitung oder Nutzung Ihrer personenbezogenen Daten, bei Auskünften, 
                  Berichtigung, Sperrung oder Löschung von Daten wenden Sie sich bitte an:
                </p>
                <div className="bg-[hsl(210,40%,98%)] p-4 rounded-lg">
                  <p className="font-medium text-[hsl(215,25%,15%)]">
                    Verein Väternotruf.com<br />
                    Rabenhof 16<br />
                    3525 Sallingberg<br />
                    Österreich
                  </p>
                  <div className="mt-3 space-y-1">
                    <p className="flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      datenschutz@vaeternotruf.com
                    </p>
                    <p className="flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      0660 233 1560
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>
    </div>
  );
};

export default Datenschutz;
